import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, ListGroup, Tab, Nav, Form, InputGroup, Modal } from 'react-bootstrap';
import { FaFileAlt, FaPlus, FaClock, FaEdit, FaEye, FaSearch, FaFilter, FaCheck, FaExclamationTriangle, FaHospital, FaUserMd, FaPills, FaClipboardList, FaHome, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import { debounce } from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';

import dynamicFormsService from '../../services/dynamicFormsService';
import patientRecordsService from '../../services/patientRecordsService';
import formSubmissionActionsService from '../../services/formSubmissionActionsService';
import rbacService from '../../services/rbacService';
import CustomPagination from '../common/CustomPagination';
import ClinicalFormsSearch from './ClinicalFormsSearch';
import config from '../../config/config';
import { addRecentForm, addRecentSearch, trackFormAccess, addNavigationHistory, addPatientToContext } from '../../redux/slices/navigationSlice';
import { getCurrentUser, userHasRole } from '../../utils/authUtils';
import '../componentCss/patientFormsHub.css';

const PatientFormsHub = ({ patient, onClose, viewMode = 'edit', isReadOnly = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    
    // Get current user for permission checks
    const currentUser = getCurrentUser();

    // Check if current user has parent role - parents can only view forms with submissions
    const isParentRole = userHasRole('parent');

    // Redux state
    const navigationState = useSelector(state => state.navigation);
    const recentFormsFromRedux = navigationState?.recentForms || [];

    // Local state
    const [forms, setForms] = useState([]);
    const [folders, setFolders] = useState([]);
    const [filteredForms, setFilteredForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('all');
    const [draftForms, setDraftForms] = useState([]);
    const [assignedForms, setAssignedForms] = useState([]);
    const [recentForms, setRecentForms] = useState([]);
    const [formPermissions, setFormPermissions] = useState({});
    const [formSubmissions, setFormSubmissions] = useState({});
    
    // Submission viewing modal states
    const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
    const [selectedFormForSubmissions, setSelectedFormForSubmissions] = useState(null);

    // Ref to track the last search term that triggered an API call
    const lastSearchRef = useRef('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [totalForms, setTotalForms] = useState(0);
    const [isLoadingPaginated, setIsLoadingPaginated] = useState(false);

    // Default folder categories - these will be replaced by actual folders from API
    const defaultFolderCategories = [
        { id: 'all', name: 'All Forms', icon: FaClipboardList, color: 'primary' },
        { id: 'no-folder', name: 'Uncategorized', icon: FaFileAlt, color: 'secondary' }
    ];

    // Temporary placeholder - will define handlers after loadPatientForms
    
    // Cache for user permissions (loaded once)
    const userPermissionsRef = useRef(null);
    const initializedPatientRef = useRef(null); // Track which patient we've initialized
    const [allPatientSubmissions, setAllPatientSubmissions] = useState([]);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    // Compute display forms based on role - parent role only sees forms with submissions
    // For parent role, also apply client-side pagination since we fetch all forms
    const displayForms = useMemo(() => {
        if (isParentRole) {
            // Filter to forms with submissions
            const formsWithSubmissions = filteredForms.filter(form =>
                formSubmissions[form.form_id]?.length > 0
            );

            // Apply client-side pagination for parent role
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            return formsWithSubmissions.slice(startIndex, endIndex);
        }
        return filteredForms;
    }, [isParentRole, filteredForms, formSubmissions, currentPage, itemsPerPage]);

    // Calculate total forms for pagination - for parent role, count only forms with submissions
    const displayTotalForms = useMemo(() => {
        if (isParentRole) {
            return filteredForms.filter(form =>
                formSubmissions[form.form_id]?.length > 0
            ).length;
        }
        return totalForms;
    }, [isParentRole, filteredForms, formSubmissions, totalForms]);

    // Load user permissions once and cache them
    const loadUserPermissions = useCallback(async (forceRefresh = false) => {
        if (!currentUser) return null;
        
        // If we have cached permissions and not forcing refresh, return cached
        if (userPermissionsRef.current && !forceRefresh) {
            return userPermissionsRef.current;
        }

        try {
            // Clear cache to get fresh permissions
            rbacService.clearUserCache(currentUser.id);
            rbacService.clearCache(); // Clear all caches to force fresh data

            const permissions = await rbacService.getUserComprehensivePermissions(currentUser.id);
            console.log('✅ Loaded permissions:', permissions);
            userPermissionsRef.current = permissions;
            return permissions;
        } catch (error) {
            console.error('Error loading user permissions:', error);
            return null;
        }
    }, [currentUser]);

    // Load all patient submissions once
    const loadAllPatientSubmissions = useCallback(async () => {
        if (!patient) return;

        try {
            // Use the dynamic forms API to get ALL submissions for this patient
            const response = await dynamicFormsService.getPatientSubmissions(patient.id, {
                limit: 1000 // Get all submissions in one call
            });
            
            if (response.success) {
                const submissions = response.data?.submissions || response.submissions || [];
                setAllPatientSubmissions(submissions);
                
                // Group submissions by form_id for easy lookup
                const submissionsByForm = {};
                submissions.forEach(submission => {
                    const formId = submission.form_id;
                    if (!submissionsByForm[formId]) {
                        submissionsByForm[formId] = [];
                    }
                    submissionsByForm[formId].push(submission);
                });
                setFormSubmissions(submissionsByForm);
                
                console.log(`✅ Loaded ${submissions.length} submissions for patient in ONE query`);
            }
        } catch (error) {
            console.error('Error loading patient submissions:', error);
            // If the endpoint doesn't exist, fall back to empty state
            setFormSubmissions({});
        }
    }, [patient]);

    // Check if user has permission to fill a specific form (using cached permissions)
    const checkFormFillPermission = useCallback((formId, permissions) => {
        if (!currentUser || !permissions) {
            console.log(`❌ No current user or permissions for form ${formId}`);
            return false;
        }

        try {
            // Early return for nurses/doctors (privileged users)
            const userRoles = currentUser.roles || [currentUser.role];
            if (userRoles.includes('nurse') || userRoles.includes('doctor') || 
                userRoles.includes('physician') || userRoles.includes('super_admin')) {
                console.log(`📋 Form ${formId}: Privileged user - full access`);
                return true;
            }

            // Method 1: Use pre-calculated form_permissions array (most accurate)
            const formPermissions = permissions.data?.form_permissions || [];
            console.log(`🔍 Method 1 - Checking form_permissions array:`, {
                formId,
                arrayLength: formPermissions.length,
                formIds: formPermissions.map(fp => fp.form_id)
            });

            const formPerm = formPermissions.find(fp => fp.form_id === formId);
            if (formPerm) {
                const canEdit = formPerm.can_edit || formPerm.can_submit || formPerm.can_view || false;
                console.log(`📋 Form ${formId}: Found in form_permissions array, can_edit=${canEdit}`, formPerm);
                return canEdit;
            }

            console.log(`⚠️ Form ${formId}: NOT found in form_permissions array, falling back to Method 2`);

            // Method 2: Check effective_permissions dict as fallback
            const effectivePermissions = permissions.data?.effective_permissions || {};

            console.log(`📋 Form ${formId}: Checking effective_permissions`, {
                availablePermissions: Object.keys(effectivePermissions),
                checkingFor: [
                    `forms.${formId}.edit`,
                    `forms.${formId}.create`,
                    `forms.${formId}.view`,
                    'forms.*.edit',
                    'forms.*.create',
                    'forms.*.view',
                    'form.submit'
                ],
                actualValues: {
                    'forms.*.view': effectivePermissions['forms.*.view'],
                    [`forms.${formId}.edit`]: effectivePermissions[`forms.${formId}.edit`],
                    [`forms.${formId}.view`]: effectivePermissions[`forms.${formId}.view`]
                }
            });
            
            // Check for general form edit/submit permission
            if (effectivePermissions['forms.*.edit']) {
                console.log(`✅ Form ${formId}: Has wildcard forms.*.edit`);
                return true;
            }

            if (effectivePermissions['forms.*.create']) {
                console.log(`✅ Form ${formId}: Has wildcard forms.*.create`);
                return true;
            }

            const formsWildcardView = effectivePermissions['forms.*.view'];
            console.log(`🔍 CHECKING forms.*.view [v2]:`, {
                key: 'forms.*.view',
                value: formsWildcardView,
                typeOf: typeof formsWildcardView,
                truthyCheck: !!formsWildcardView,
                willEnterIfBlock: formsWildcardView === true
            });

            if (formsWildcardView) {
                console.log(`✅✅✅ MATCHED! Form ${formId}: Has wildcard forms.*.view - RETURNING TRUE NOW`);
                return true;
            }

            console.log(`❌ Did NOT enter if block for forms.*.view despite value being:`, formsWildcardView);
            
            if (effectivePermissions['form.submit']) {
                console.log(`✅ Form ${formId}: Has form.submit`);
                return true;
            }

            // Check for specific form permission
            const specificEditPerm = `forms.${formId}.edit`;
            const specificCreatePerm = `forms.${formId}.create`;
            const specificViewPerm = `forms.${formId}.view`;

            console.log(`📋 Form ${formId}: Looking for ${specificEditPerm}, ${specificCreatePerm}, or ${specificViewPerm}`);

            if (effectivePermissions[specificEditPerm]) {
                console.log(`✅ Form ${formId}: Has specific forms.{formId}.edit`, effectivePermissions[specificEditPerm]);
                return true;
            }

            if (effectivePermissions[specificCreatePerm]) {
                console.log(`✅ Form ${formId}: Has specific forms.{formId}.create`, effectivePermissions[specificCreatePerm]);
                return true;
            }

            if (effectivePermissions[specificViewPerm]) {
                console.log(`✅ Form ${formId}: Has specific forms.{formId}.view`, effectivePermissions[specificViewPerm]);
                return true;
            }

            console.log(`❌ Form ${formId}: No fill permission found`);
            console.log(`DEBUG: effectivePermissions keys:`, Object.keys(effectivePermissions));
            console.log(`DEBUG: Looking for "${specificEditPerm}" in:`, effectivePermissions);
            return false;
        } catch (error) {
            console.error(`Error checking form permission for ${formId}:`, error);
            return false;
        }
    }, [currentUser, isReadOnly]);

    // Batch process permissions for all forms (runs once when forms load)
    const processFormPermissions = useCallback(async (forms) => {
        console.log('🔐 PROCESS_FORM_PERMISSIONS CALLED with', forms?.length || 0, 'forms');
        
        if (!forms || forms.length === 0 || !currentUser) {
            console.log('❌ Early return from processFormPermissions - forms:', forms?.length, 'currentUser:', !!currentUser);
            return;
        }

        // Load permissions once
        console.log('⏳ Loading user permissions in processFormPermissions...');
        const permissions = await loadUserPermissions();
        console.log('✅ Permissions loaded:', !!permissions);
        
        if (!permissions) {
            console.error('❌ Failed to load user permissions');
            return;
        }

        console.log('🔐 Processing permissions for forms:', {
            formCount: forms.length,
            userId: currentUser.id,
            fullPermissionsObject: permissions,
            permissionsStructure: {
                hasData: !!permissions.data,
                hasFormPermissions: !!(permissions.data?.form_permissions),
                formPermissionsCount: permissions.data?.form_permissions?.length || 0,
                hasEffectivePermissions: !!(permissions.data?.effective_permissions),
                effectivePermissionsKeys: Object.keys(permissions.data?.effective_permissions || {}).length
            }
        });

        // Check permissions for all forms at once
        const permissionsMap = {};
        forms.forEach(form => {
            console.log(`🔐 Checking permission for form: ${form.form_id}`);
            if (form.form_id) {
                permissionsMap[form.form_id] = checkFormFillPermission(form.form_id, permissions);
            }
        });
        
        console.log('✅ Permission check results:', permissionsMap);
        setFormPermissions(permissionsMap);
    }, [currentUser, checkFormFillPermission]);

    // Process permissions when forms change (batch process, no individual queries)
    useEffect(() => {
        // Skip permission processing for parent role - they can only view, not fill forms
        if (isParentRole) {
            console.log('👪 Parent role - skipping form fill permission checks');
            return;
        }
        if (filteredForms.length > 0 && permissionsLoaded) {
            console.log('✅ Processing permissions for loaded forms...');
            processFormPermissions(filteredForms);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredForms, permissionsLoaded, isParentRole]);

    // Cleanup will be added after debouncedSearch is defined

    // Handle quick action navigation from dashboard
    useEffect(() => {
        if (location.state?.quickAction && location.state?.formType) {
            const formType = location.state.formType;

            // Map form types to folder names (will be matched with actual folder IDs later)
            const formTypeToFolderName = {
                'admission': 'Admission & Assessment',
                'assessment': 'Admission & Assessment',
                'daily': 'Daily Care Monitoring',
                'vitals': 'Daily Care Monitoring',
                'medication': 'Treatment & Procedures',
                'geriatric': 'Geriatric Care',
                'pediatric': 'Pediatric Care',
                'department': 'all'
            };

            const targetFolderName = formTypeToFolderName[formType] || 'all';

            // Find the actual folder ID by name (this will work after folders are loaded)
            if (targetFolderName === 'all') {
                setSelectedFolder('all');
            } else {
                // We'll need to wait for folders to load to find the correct ID
                // For now, set to 'all' and let the user manually select
                setSelectedFolder('all');

                // Set a timeout to try to match the folder after folders are loaded
                setTimeout(() => {
                    const matchingFolder = folders.find(folder => folder.name === targetFolderName);
                    if (matchingFolder) {
                        setSelectedFolder(matchingFolder.folder_id);
                    }
                }, 1000);
            }

            // Set search term to help filter forms
            if (formType === 'vitals') {
                setSearchTerm('vital');
            } else if (formType === 'medication') {
                setSearchTerm('medication');
            } else if (formType === 'geriatric') {
                setSearchTerm('geriatric');
            } else if (formType === 'pediatric') {
                setSearchTerm('pediatric');
            } else if (formType === 'assessment') {
                setSearchTerm('assessment');
            } else if (formType === 'admission') {
                setSearchTerm('admission');
            } else if (formType === 'daily') {
                setSearchTerm('daily');
            }
        }
    }, [location.state]);

    // New paginated forms loading function
    const loadPatientForms = async (resetToFirstPage = false, specificPage = null) => {
        if (!patient?.id) {
            console.log('❌ No patient ID available');
            return;
        }

        try {
            setIsLoadingPaginated(true);
            setError(null);

            // Clear forms to prevent showing old data
            if (resetToFirstPage) {
                setForms([]);
                setCurrentPage(1);
            }

            // Calculate skip value for pagination - use specificPage if provided, otherwise use current state
            const pageToUse = resetToFirstPage ? 1 : (specificPage || currentPage);
            const skip = (pageToUse - 1) * itemsPerPage;

            // For parent role, fetch ALL forms (we'll filter by submissions and paginate client-side)
            // For other roles, use server-side pagination as before
            const params = isParentRole ? {
                skip: 0,
                limit: 1000, // Fetch all forms for client-side filtering
                ...(searchTerm && searchTerm.trim().length >= 2 && { search: searchTerm.trim() }),
                ...(selectedFolder !== 'all' && selectedFolder !== 'no-folder' && { folder_id: selectedFolder })
            } : {
                skip: skip,
                limit: itemsPerPage,
                // Only include search parameter if searchTerm has at least 2 characters
                ...(searchTerm && searchTerm.trim().length >= 2 && { search: searchTerm.trim() }),
                ...(selectedFolder !== 'all' && selectedFolder !== 'no-folder' && { folder_id: selectedFolder })
            };

            console.log('🚀 Loading patient forms with pagination:', {
                patientId: patient.id,
                pageToUse,
                itemsPerPage,
                calculatedSkip: skip,
                expectedRange: `${skip + 1}-${skip + itemsPerPage}`,
                params
            });

            // Use the new paginated endpoint
            const response = await patientRecordsService.getPatientClinicalForms(patient.id, params);

            console.log('📥 Paginated forms response:', response);

            if (response.success) {
                const formsData = response.items || [];
                setForms(formsData);
                setTotalForms(response.total);

                // Clear filteredForms since we're using server-side pagination now
                setFilteredForms(formsData);

                console.log('✅ Forms loaded successfully:', {
                    count: formsData.length,
                    total: response.total,
                    page: pageToUse
                });
            } else {
                setError(response.error || 'Failed to load patient forms');
                setForms([]);
                setTotalForms(0);
                setFilteredForms([]);
            }
        } catch (err) {
            console.error('💥 Error loading patient forms:', err);
            setError('Error loading forms. Please try again.');
            setForms([]);
            setTotalForms(0);
            setFilteredForms([]);
        } finally {
            setIsLoadingPaginated(false);
            setLoading(false);
        }
    };

    // Pagination handlers
    const handlePageChange = async (newPage) => {
        console.log('🔄 CLINICAL FORMS PAGINATION - Page change requested:', newPage);
        console.log('📍 Current page before change:', currentPage);
        console.log('🧮 Skip calculation will be:', (newPage - 1) * itemsPerPage);
        console.log('🎯 Expected range:', `${((newPage - 1) * itemsPerPage) + 1}-${newPage * itemsPerPage}`);

        if (newPage === currentPage || isLoadingPaginated) {
            console.log('❌ Skipping page change - same page or loading');
            return;
        }

        setCurrentPage(newPage);

        // For parent role, pagination is client-side (all forms already loaded)
        // No need to fetch from server again
        if (isParentRole) {
            console.log('👪 Parent role - using client-side pagination');
            return;
        }

        setForms([]); // Clear forms to show loading state
        await loadPatientForms(false, newPage); // Pass the new page explicitly
    };

    const handleItemsPerPageChange = async (newItemsPerPage) => {
        console.log('🔄 CLINICAL FORMS - Items per page change:', newItemsPerPage);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        setForms([]); // Clear forms to show loading state
        await loadPatientForms(true, 1); // Explicitly pass page 1
    };

    // Debounced search functionality (now after loadPatientForms is defined)
    const debouncedSearch = useCallback(
        debounce(async (query) => {
            if (!patient) return;

            // Check if this is the same query we already processed
            if (lastSearchRef.current === query) {
                console.log('🔍 SKIPPING DUPLICATE SEARCH for:', query);
                return;
            }

            // Only trigger search if there's a query with at least 2 characters
            if (query && query.trim().length >= 2) {
                console.log('🔍 DEBOUNCED SEARCH triggered with:', query);
                lastSearchRef.current = query;
                loadPatientForms(true); // Reset to first page on search
            } else if (query === '' && lastSearchRef.current !== '') {
                // Only reload if we're clearing a previous search
                console.log('🔍 CLEARING SEARCH - reloading forms');
                lastSearchRef.current = '';
                loadPatientForms(true);
            }
        }, 300), // Reduced to 300ms for more responsive feel
        [patient, loadPatientForms] // Stable dependencies
    );

    // Handle search input changes
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedSearch(value);
    };

    // Handle folder selection changes
    const handleFolderChange = (folderId) => {
        console.log('🏢 Folder filter changed:', folderId);
        setSelectedFolder(folderId);
        if (patient) {
            loadPatientForms(true); // Reset to first page when folder changes
        }
    };

    // Cleanup debounced search
    useEffect(() => {
        return () => {
            if (debouncedSearch) {
                debouncedSearch.cancel();
            }
        };
    }, [debouncedSearch]);

    // Load permissions first when patient changes
    useEffect(() => {
        if (!patient?.id || !currentUser?.id) return;
        
        // Check if we've already loaded permissions for this patient
        if (initializedPatientRef.current === patient.id && permissionsLoaded) {
            console.log('✅ Permissions already loaded for this patient');
            return;
        }
        
        console.log('⏳ Loading permissions for patient:', patient.id);
        
        const loadPerms = async () => {
            try {
                rbacService.clearUserCache(currentUser.id);
                const perms = await rbacService.getUserComprehensivePermissions(currentUser.id);
                if (perms) {
                    setPermissionsLoaded(true);
                    console.log('✅ Permissions loaded');
                }
            } catch (error) {
                console.error('❌ Error loading permissions:', error);
            }
        };
        
        loadPerms();
    }, [patient?.id, currentUser?.id]);

    // Load forms whenever permissions are loaded or patient changes
    useEffect(() => {
        if (!patient?.id || !permissionsLoaded) {
            console.log('⏳ Waiting for permissions before loading forms...');
            return;
        }
        
        console.log('📋 Permissions ready! Loading forms now');
        
        const loadInitialData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Load forms using loadPatientForms to ensure consistent behavior
                // (parent role gets all forms with limit 1000, other roles get paginated with itemsPerPage)
                console.log('🚀 Fetching patient forms via loadPatientForms...');
                await loadPatientForms(true);

                // Load folders
                console.log('🚀 Fetching folders...');
                const foldersResponse = await dynamicFormsService.getFolders();
                if (foldersResponse.success) {
                    setFolders(Array.isArray(foldersResponse.data) ? foldersResponse.data : []);
                    console.log('✅ Folders loaded');
                }
                
                // Load submissions
                console.log('🚀 Fetching submissions...');
                const submissionsResponse = await dynamicFormsService.getPatientSubmissions(patient.id, { limit: 1000 });
                if (submissionsResponse.success) {
                    const submissions = submissionsResponse.data?.submissions || submissionsResponse.submissions || [];
                    const submissionsByForm = {};
                    submissions.forEach(submission => {
                        const formId = submission.form_id;
                        if (!submissionsByForm[formId]) {
                            submissionsByForm[formId] = [];
                        }
                        submissionsByForm[formId].push(submission);
                    });
                    setFormSubmissions(submissionsByForm);
                    console.log('✅ Submissions loaded:', submissions.length);
                }
                
                console.log('✅ All data loaded successfully!');
            } catch (error) {
                console.error('❌ Error loading data:', error);
                setError('Error loading data');
            } finally {
                setIsLoadingPaginated(false);
                setLoading(false);
            }
        };
        
        loadInitialData();
    }, [patient, permissionsLoaded, itemsPerPage]);

    const loadFolders = async () => {
        try {
            // Load all available folders
            const response = await dynamicFormsService.getFolders();

            if (response.success) {
                const foldersData = Array.isArray(response.data) ? response.data : [];
                setFolders(foldersData);
            } else {
                console.error('Failed to load folders:', response.message);
                // Set empty folders array if loading fails
                setFolders([]);
            }
        } catch (err) {
            console.error('Error loading folders:', err);
            setFolders([]);
        }
    };

    // TODO: Fix drafts functionality later - commenting out for now
    // const loadDraftForms = async () => {
    //     try {
    //         if (!patient?.id) return;
    //         
    //         const response = await dynamicFormsService.getPatientDrafts(patient.id);
    //         if (response.success) {
    //             const drafts = response.data || [];
    //             
    //             // Transform drafts to include form names and required UI data
    //             const enrichedDrafts = await Promise.all(drafts.map(async (draft) => {
    //                 try {
    //                     const formResponse = await dynamicFormsService.getFormById(draft.form_id);
    //                     const formName = formResponse.success ? (formResponse.data.name || formResponse.data.formName) : 'Unknown Form';
    //                     
    //                     return {
    //                         ...draft,
    //                         formName: formName,
    //                         lastModified: draft.draft_saved_at || draft.submitted_at,
    //                         completionPercentage: draft.completion_percentage || 0,
    //                         id: draft.submission_id
    //                     };
    //                 } catch (err) {
    //                     console.error('Error enriching draft:', err);
    //                     return {
    //                         ...draft,
    //                         formName: 'Form',
    //                         lastModified: draft.draft_saved_at || draft.submitted_at,
    //                         completionPercentage: 0,
    //                         id: draft.submission_id
    //                     };
    //                 }
    //             }));
    //             
    //             setDraftForms(enrichedDrafts);
    //         } else {
    //             console.error('Failed to load drafts:', response.message);
    //             setDraftForms([]);
    //         }
    //     } catch (err) {
    //         console.error('Error loading draft forms:', err);
    //         setDraftForms([]);
    //     }
    // };

    // TODO: Commented out recent forms functionality
    // const loadRecentForms = async () => {
    //     try {
    //         if (!patient?.id) return;
    //         
    //         const response = await dynamicFormsService.getPatientRecentSubmissions(patient.id, 10);
    //         if (response.success) {
    //             const submissions = response.data || [];
    //             
    //             // Transform submissions to include form names and required UI data
    //             const enrichedSubmissions = await Promise.all(submissions.map(async (submission) => {
    //                 try {
    //                     const formResponse = await dynamicFormsService.getFormById(submission.form_id);
    //                     const formName = formResponse.success ? (formResponse.data.name || formResponse.data.formName) : 'Unknown Form';
    //                     
    //                     return {
    //                         ...submission,
    //                         formName: formName,
    //                         completedDate: submission.submitted_at,
    //                         completedBy: submission.submitted_by || 'Unknown',
    //                         id: submission.submission_id
    //                     };
    //                 } catch (err) {
    //                     console.error('Error enriching submission:', err);
    //                     return {
    //                         ...submission,
    //                         formName: 'Form',
    //                         completedDate: submission.submitted_at,
    //                         completedBy: 'Unknown',
    //                         id: submission.submission_id
    //                     };
    //                 }
    //             }));
    //             
    //             setRecentForms(enrichedSubmissions);
    //         } else {
    //             console.error('Failed to load recent submissions:', response.message);
    //             setRecentForms([]);
    //         }
    //     } catch (err) {
    //         console.error('Error loading recent forms:', err);
    //         setRecentForms([]);
    //     }
    // };

    const isFormRelevantToPatient = (form, patient) => {
        // Advanced logic to determine if a form is relevant to the patient
        // Based on patient status, department, conditions, medications, etc.

        if (!patient) return false;

        const patientStatus = patient.care_status?.status || patient.status;
        const patientDepartmentId = patient.department_id || patient.care_status?.department_id;
        const patientDepartmentName = patient.department || patient.care_status?.department || 'general';
        const patientAge = calculateAge(patient.demographics?.date_of_birth || patient.date_of_birth);
        const patientConditions = patient.medical_record?.medical_conditions || [];
        const patientMedications = patient.medical_record?.current_medications || [];
        const formName = form.name?.toLowerCase() || form.formName?.toLowerCase() || '';

        // Smart recommendations based on patient status
        if (patientStatus === 'pre_registered' || patientStatus === 4) {
            // Pre-registered patients need admission and assessment forms
            return formName.includes('admission') ||
                formName.includes('assessment') ||
                formName.includes('intake') ||
                formName.includes('initial') ||
                formName.includes('registration');
        }

        if (patientStatus === 'active' || patientStatus === 1) {
            // Active patients need daily care, monitoring, and treatment forms
            if (formName.includes('daily') || formName.includes('vital') ||
                formName.includes('monitoring') || formName.includes('medication')) {
                return true;
            }
        }

        // Department-specific recommendations - Check by ID first, then fallback to name
        if (patientDepartmentId && form.department_id) {
            // Match by ObjectId if available
            if (patientDepartmentId === form.department_id) {
                return true;
            }
        } else if (patientDepartmentName && patientDepartmentName !== 'general') {
            // Fallback to name-based matching for legacy compatibility
            if (formName.includes(patientDepartmentName.toLowerCase()) ||
                (form.department && form.department.toLowerCase() === patientDepartmentName.toLowerCase())) {
                return true;
            }
        }

        // Age-based recommendations
        if (patientAge < 18 && formName.includes('pediatric')) {
            return true;
        }
        if (patientAge >= 65 && formName.includes('geriatric')) {
            return true;
        }

        // Condition-based recommendations
        if (patientConditions.length > 0) {
            for (const condition of patientConditions) {
                const conditionName = condition.condition?.toLowerCase() || condition.name?.toLowerCase();
                if (conditionName && formName.includes(conditionName)) {
                    return true;
                }
            }
        }

        // Medication-based recommendations
        if (patientMedications.length > 0 && formName.includes('medication')) {
            return true;
        }

        // Default to showing general forms
        return !formName.includes('specific') && !formName.includes('specialty');
    };

    const calculateAge = (birthDate) => {
        if (!birthDate) return 0;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const getFolderForForm = (form) => {
        // If form has a folder_id, find the corresponding folder
        if (form.folder_id) {
            const folder = folders.find(f => f.folder_id === form.folder_id);
            return folder ? folder : null;
        }
        return null;
    };

    const getFolderCategories = () => {
        // Create folder categories from loaded folders
        const folderCategories = [defaultFolderCategories[0]]; // "All Forms"

        // Add categories for each folder
        folders.forEach(folder => {
            folderCategories.push({
                id: folder.folder_id,
                name: folder.name,
                icon: getFolderIcon(folder.name),
                color: getFolderColor(folder.name),
                description: folder.description
            });
        });

        // Add "Uncategorized" for forms without folders
        folderCategories.push(defaultFolderCategories[1]);

        return folderCategories;
    };

    const getFolderIcon = (folderName) => {
        const name = folderName.toLowerCase();
        if (name.includes('admission') || name.includes('assessment')) return FaHospital;
        if (name.includes('daily') || name.includes('monitoring')) return FaUserMd;
        if (name.includes('treatment') || name.includes('procedure')) return FaPills;
        if (name.includes('discharge') || name.includes('transfer')) return FaHome;
        if (name.includes('administrative') || name.includes('insurance')) return FaFileAlt;
        if (name.includes('emergency') || name.includes('urgent')) return FaExclamationTriangle;
        if (name.includes('geriatric') || name.includes('pediatric')) return FaUserMd;
        return FaClipboardList;
    };

    const getFolderColor = (folderName) => {
        const name = folderName.toLowerCase();
        if (name.includes('admission') || name.includes('assessment')) return 'success';
        if (name.includes('daily') || name.includes('monitoring')) return 'info';
        if (name.includes('treatment') || name.includes('procedure')) return 'warning';
        if (name.includes('discharge') || name.includes('transfer')) return 'danger';
        if (name.includes('administrative') || name.includes('insurance')) return 'secondary';
        if (name.includes('emergency') || name.includes('urgent')) return 'danger';
        if (name.includes('geriatric') || name.includes('pediatric')) return 'info';
        return 'primary';
    };

    // Removed filterForms function since we're using server-side pagination

    const handleFillForm = (form) => {
        if (!form || !patient) return;

        // Track form access in Redux
        dispatch(trackFormAccess({
            formId: form.form_id || form.id || form._id
        }));

        // Add to recent forms
        dispatch(addRecentForm(form));

        // Add navigation history
        dispatch(addNavigationHistory({
            path: location.pathname,
            label: `Form: ${form.name || form.formName}`
        }));

        // Navigate to form submission with patient context
        let formId = form.form_id || form.id || form._id;

        // Handle formId properly - it might be an object
        if (typeof formId === 'object') {
            if (formId._id) {
                formId = formId._id;
            } else if (formId.$oid) {
                formId = formId.$oid;
            } else if (formId.id) {
                formId = formId.id;
            } else {
                toast.error('Invalid form ID format. Please try again.');
                return;
            }
        }

        // Handle formDetailId properly - it might be an object
        let formDetailId = 'new';
        if (form.formDetailId) {
            if (typeof form.formDetailId === 'object' && form.formDetailId._id) {
                formDetailId = form.formDetailId._id;
            } else if (typeof form.formDetailId === 'object' && form.formDetailId.$oid) {
                formDetailId = form.formDetailId.$oid;
            } else if (typeof form.formDetailId === 'string') {
                formDetailId = form.formDetailId;
            }
        }

        const formSubmissionUrl = `/form-submission/${formId}/${formDetailId}/${patient.id}`;

        // Form validation data
        const debugData = {
            rawForm: form,
            extractedFormId: formId,
            formDetailId: formDetailId,
            patientId: patient.id,
            finalUrl: formSubmissionUrl
        };

        // Extra validation
        if (!formId || typeof formId === 'object' || formId.toString().includes('object') || formId.toString().includes('[object')) {
            toast.error('Invalid form ID. Please try again.');
            return;
        }

        // Ensure formId is a string and not empty
        formId = formId.toString().trim();
        if (!formId || formId === 'undefined' || formId === 'null') {
            toast.error('Invalid form ID. Please try again.');
            return;
        }

        navigate(formSubmissionUrl);
    };

    const handleViewForm = (form) => {
        if (!form) return;

        // Navigate to form structure view
        const formId = form.form_id || form.id;

        // Ensure formId is a string
        if (typeof formId === 'object' && formId._id) {
            navigate(`/view-form-structure/${formId._id}`);
        } else {
            navigate(`/view-form-structure/${formId}`);
        }
    };

    const handleViewSubmissions = (form) => {
        if (!form || !patient) return;

        setSelectedFormForSubmissions(form);
        setShowSubmissionsModal(true);
    };

    const handleViewSubmissionDetails = (submission) => {
        if (!submission || !patient) return;

        const formId = selectedFormForSubmissions?.form_id || selectedFormForSubmissions?.id;
        const submissionId = submission.submission_id || submission.id;
        
        // Navigate to ModernFormSubmission component in view mode
        navigate(`${config.baseUrl}form-submission/${formId}/${submissionId}/${patient.id}/view`);
    };

    const handleCloseSubmissionsModal = () => {
        setShowSubmissionsModal(false);
        setSelectedFormForSubmissions(null);
    };

    // TODO: Fix drafts functionality later - commenting out for now
    // const handleContinueDraft = (draft) => {
    //     // Navigate to continue draft form
    //     const formId = draft.form_id || draft.formId;
    //     const draftId = draft.id;
    //     
    //     // Ensure IDs are strings
    //     const safeFormId = typeof formId === 'object' ? formId._id : formId;
    //     const safeDraftId = typeof draftId === 'object' ? draftId._id : draftId;
    //     
    //     navigate(`/form-submission/${safeFormId}/${safeDraftId}/${patient.id}?draft=true`);
    // };

    // TODO: Commented out recent forms functionality
    // const handleViewRecentForm = (recentForm) => {
    //     // Navigate to view completed form
    //     const formId = recentForm.form_id || recentForm.formId;
    //     const formName = recentForm.form_name || recentForm.formName;
    //     const recentId = recentForm.id;
    //     
    //     // Ensure IDs are strings
    //     const safeFormId = typeof formId === 'object' ? formId._id : formId;
    //     const safeRecentId = typeof recentId === 'object' ? recentId._id : recentId;
    //     
    //     navigate(`/viewRecord/${safeFormId}/${safeRecentId}/${formName}`);
    // };

    const getFormFolderInfo = (form) => {
        const folder = getFolderForForm(form);
        const folderCategories = getFolderCategories();

        if (folder) {
            const folderInfo = folderCategories.find(cat => cat.id === folder.folder_id);
            return folderInfo || folderCategories[0]; // Default to 'all'
        } else {
            // Form without folder - return "Uncategorized"
            return folderCategories.find(cat => cat.id === 'no-folder') || folderCategories[0];
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getFormPriority = (form, patient) => {
        const formName = (form.name?.toLowerCase() || form.formName?.toLowerCase() || '');
        const patientStatus = patient?.care_status?.status || patient?.status;
        const patientAge = calculateAge(patient?.demographics?.date_of_birth || patient?.date_of_birth);

        // High priority forms
        if (patientStatus === 'pre_registered' || patientStatus === 4) {
            if (formName.includes('admission') || formName.includes('assessment')) {
                return 'high';
            }
        }

        // Age-based high priority
        if (patientAge >= 65 && formName.includes('geriatric')) {
            return 'high';
        }
        if (patientAge < 18 && formName.includes('pediatric')) {
            return 'high';
        }

        // Medium priority for daily monitoring
        if (formName.includes('daily') || formName.includes('vital') || formName.includes('monitoring')) {
            return 'medium';
        }

        return 'low';
    };

    const getRecommendationReason = (form, patient) => {
        const formName = (form.name?.toLowerCase() || form.formName?.toLowerCase() || '');
        const patientStatus = patient?.care_status?.status || patient?.status;
        const patientDepartment = patient?.department || patient?.care_status?.department || 'general';
        const patientAge = calculateAge(patient?.demographics?.date_of_birth || patient?.date_of_birth);

        if (patientStatus === 'pre_registered' || patientStatus === 4) {
            if (formName.includes('admission')) return 'New patient admission';
            if (formName.includes('assessment')) return 'Initial assessment required';
        }

        if (patientStatus === 'active' || patientStatus === 1) {
            if (formName.includes('daily')) return 'Daily monitoring required';
            if (formName.includes('vital')) return 'Vital signs tracking';
            if (formName.includes('medication')) return 'Medication management';
        }

        if (patientDepartment !== 'general' && formName.includes(patientDepartment.toLowerCase())) {
            return `${patientDepartment} department form`;
        }

        if (patientAge >= 65 && formName.includes('geriatric')) {
            return 'Senior patient care';
        }
        if (patientAge < 18 && formName.includes('pediatric')) {
            return 'Pediatric patient care';
        }

        return 'General patient care';
    };

    const getSmartRecommendationReason = (patient) => {
        const patientStatus = patient?.care_status?.status || patient?.status;
        const patientDepartment = patient?.department || patient?.care_status?.department || 'general';
        const patientAge = calculateAge(patient?.demographics?.date_of_birth || patient?.date_of_birth);

        if (patientStatus === 'pre_registered' || patientStatus === 4) {
            return 'New Admission';
        }
        if (patientStatus === 'active' || patientStatus === 1) {
            return 'Active Care';
        }
        if (patientAge >= 65) {
            return 'Senior Care';
        }
        if (patientAge < 18) {
            return 'Pediatric Care';
        }
        if (patientDepartment !== 'general') {
            return `${patientDepartment} Dept`;
        }
        return 'General Care';
    };

    // TODO: Fix drafts functionality later - commenting out for now
    // Progress tracking helper functions
    // const getProgressStatus = (percentage) => {
    //     if (percentage >= 90) {
    //         return { color: 'success', label: 'Complete' };
    //     } else if (percentage >= 70) {
    //         return { color: 'warning', label: 'Nearly Done' };
    //     } else if (percentage >= 30) {
    //         return { color: 'info', label: 'In Progress' };
    //     } else {
    //         return { color: 'secondary', label: 'Just Started' };
    //     }
    // };

    // const getFormUrgency = (lastModified) => {
    //     const now = new Date();
    //     const modified = new Date(lastModified);
    //     const hoursDiff = (now - modified) / (1000 * 60 * 60);
    //     
    //     if (hoursDiff > 48) {
    //         return { isUrgent: true, label: 'Stale', priority: 'high' };
    //     } else if (hoursDiff > 24) {
    //         return { isUrgent: true, label: 'Old', priority: 'medium' };
    //     }
    //     return { isUrgent: false, label: 'Recent', priority: 'low' };
    // };

    // const getEstimatedTime = (completionPercentage) => {
    //     if (completionPercentage >= 90) {
    //         return '2-3 minutes';
    //     } else if (completionPercentage >= 70) {
    //         return '5-7 minutes';
    //     } else if (completionPercentage >= 50) {
    //         return '10-15 minutes';
    //     } else if (completionPercentage >= 30) {
    //         return '15-20 minutes';
    //     } else {
    //         return '20-30 minutes';
    //     }
    // };

    // TODO: Fix drafts functionality later - commenting out for now
    // const handlePreviewDraft = (draft) => {
    //     // Navigate to preview mode for draft
    //     navigate(`/form-preview/${draft.formId}/${draft.id}/${patient.id}?mode=draft`);
    // };

    // Handler for topbar navigation
    const handleTopbarFolderSelect = (folderId) => {
        setSelectedFolder(folderId);
    };

    const handleTopbarFormSelect = (form) => {
        handleFillForm(form);
    };

    const handleTopbarSearchChange = (searchValue) => {
        setSearchTerm(searchValue);

        // Track search in Redux if it's meaningful
        if (searchValue && searchValue.length >= 2) {
            dispatch(addRecentSearch(searchValue));
        }
    };

    // Handler for patient context switching
    const handlePatientChange = (newPatient) => {
        // This would typically be handled by a parent component
        // For now, we'll just add navigation history
        dispatch(addNavigationHistory({
            path: location.pathname,
            label: `Switched to patient: ${newPatient.demographics?.full_name || newPatient.full_name}`
        }));

        // Re-load forms for the new patient context
        setSelectedFolder('all');
        setSearchTerm('');

        // Trigger data reload for new patient
        if (newPatient && newPatient.id !== patient?.id) {
            // This would ideally trigger a parent component update
            // For demo purposes, we'll show a toast
            toast.success(`Switched to ${newPatient.demographics?.full_name || newPatient.full_name}`);
        }
    };

    const handleAddPatient = () => {
        // This would open a patient search modal or navigate to patient selection
        toast.info('Patient search functionality would be implemented here');
    };

    // Add current patient to multi-patient context on mount
    React.useEffect(() => {
        if (patient) {
            dispatch(addPatientToContext(patient));
        }
    }, [patient, dispatch]);

    if (loading && !patient) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading patient forms...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="m-3">
                <FaExclamationTriangle className="me-2" />
                {error}
                <Button variant="outline-danger" size="sm" className="ms-2" onClick={loadPatientForms}>
                    Retry
                </Button>
            </Alert>
        );
    }

    return (
        <div className="patient-forms-hub">
            <Container fluid className="p-3">
                {/* Header */}
                <Row className="mb-3">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <FaFileAlt className="me-2 text-primary" />
                                Clinical Forms for {patient?.demographics?.full_name || patient?.full_name}
                            </h5>
                            {onClose && (
                                <Button variant="outline-secondary" size="sm" onClick={onClose}>
                                    <FaTimes />
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Search and Filter */}
                <Row className="mb-3">
                    <Col md={8}>
                        <ClinicalFormsSearch
                            onSearch={handleSearchChange}
                            loading={isLoadingPaginated}
                            placeholder="Search clinical forms by name, description, or keywords..."
                        />
                    </Col>
                    <Col md={4}>
                        <Form.Select
                            value={selectedFolder}
                            onChange={(e) => handleFolderChange(e.target.value)}
                            disabled={isLoadingPaginated}
                        >
                            <option value="">Filter by Category</option>
                            {getFolderCategories().map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>

                {/* Tab Navigation */}
                <Tab.Container defaultActiveKey="all-forms">
                    <Nav variant="tabs" className="mb-3">
                        <Nav.Item>
                            <Nav.Link eventKey="all-forms">
                                <FaClipboardList className="me-1" />
                                All Forms
                            </Nav.Link>
                        </Nav.Item>
                        {/* TODO: Fix drafts functionality later - commenting out for now */}
                        {/* <Nav.Item>
                        <Nav.Link eventKey="drafts">
                            <FaClock className="me-1" />
                            Drafts {draftForms.length > 0 && <Badge bg="warning" className="ms-1">{draftForms.length}</Badge>}
                        </Nav.Link>
                    </Nav.Item> */}
                        {/* TODO: Commented out recent tab functionality */}
                        {/* <Nav.Item>
                        <Nav.Link eventKey="recent">
                            <FaCheck className="me-1" />
                            Recent {recentForms.length > 0 && <Badge bg="success" className="ms-1">{recentForms.length}</Badge>}
                        </Nav.Link>
                    </Nav.Item> */}
                    </Nav>

                    <Tab.Content>

                        {/* All Forms Tab */}
                        <Tab.Pane eventKey="all-forms">
                            {/* Search Results Info */}
                            {searchTerm && searchTerm.trim().length >= 2 && (
                                <div className="search-results-info mb-3">
                                    <Alert variant="info" className="py-2">
                                        <Row className="align-items-center">
                                            <Col>
                                                {isLoadingPaginated ? (
                                                    <span>
                                                        <Spinner size="sm" className="me-2" />
                                                        Searching for "{searchTerm}"...
                                                    </span>
                                                ) : (
                                                    <span>
                                                        <FaSearch className="me-2" />
                                                        Found {displayTotalForms} form{displayTotalForms !== 1 ? 's' : ''} matching "{searchTerm}"
                                                    </span>
                                                )}
                                            </Col>
                                            <Col xs="auto">
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    onClick={() => { handleSearchChange(''); }}
                                                    disabled={isLoadingPaginated}
                                                >
                                                    <FaTimes className="me-1" />
                                                    Clear Search
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Alert>
                                </div>
                            )}

                            {/* Loading state for pagination */}
                            {isLoadingPaginated && !searchTerm && (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" size="sm" />
                                    <span className="ms-2 text-muted">Loading forms...</span>
                                </div>
                            )}

                            <Row>
                                {displayForms.length > 0 ? (
                                    displayForms.map((form, index) => {
                                        const folderInfo = getFormFolderInfo(form);
                                        const IconComponent = folderInfo.icon;

                                        return (
                                            <Col md={6} lg={4} key={index} className="mb-3">
                                                <Card className="h-100">
                                                    <Card.Body>
                                                        <div className="d-flex align-items-start mb-2">
                                                            <IconComponent className={`me-2 mt-1 text-${folderInfo.color}`} />
                                                            <div className="flex-grow-1">
                                                                <Card.Title className="h6 mb-1">{form.name || form.formName}</Card.Title>
                                                                <Badge bg={folderInfo.color} className="mb-2">
                                                                    {folderInfo.name}
                                                                </Badge>
                                                                {form.description && (
                                                                    <Card.Text className="small text-muted">
                                                                        {form.description}
                                                                    </Card.Text>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Show submission count if available */}
                                                        {formSubmissions[form.form_id]?.length > 0 && (
                                                            <div className="mb-2">
                                                                <small className="text-muted">
                                                                    <FaFileAlt className="me-1" />
                                                                    {formSubmissions[form.form_id].length} submission{formSubmissions[form.form_id].length !== 1 ? 's' : ''}
                                                                </small>
                                                            </div>
                                                        )}

                                                        <div className="d-flex gap-2 flex-column">
                                                            {/* View Submissions button - shown to everyone if submissions exist */}
                                                            {formSubmissions[form.form_id]?.length > 0 && (
                                                                <Button
                                                                    variant="outline-info"
                                                                    size="sm"
                                                                    onClick={() => handleViewSubmissions(form)}
                                                                    className="w-100"
                                                                >
                                                                    <FaEye className="me-1" />
                                                                    View Submissions ({formSubmissions[form.form_id].length})
                                                                </Button>
                                                            )}

                                                            {/* Fill Form button - only if user has permission and is not a parent */}
                                                            {!isParentRole && formPermissions[form.form_id] === true && (
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => handleFillForm(form)}
                                                                    className="w-100"
                                                                >
                                                                    <FaEdit className="me-1" />
                                                                    Fill Form
                                                                </Button>
                                                            )}

                                                            {/* View Only message for read-only users */}
                                                            {isReadOnly && !formSubmissions[form.form_id]?.length && (
                                                                <small className="text-muted text-center">
                                                                    <FaEye className="me-1" />
                                                                    View only - No submissions yet
                                                                </small>
                                                            )}

                                                            {/* No permission message - don't show for parent role */}
                                                            {!isReadOnly && !isParentRole && formPermissions[form.form_id] === false && !formSubmissions[form.form_id]?.length && (
                                                                <Alert variant="warning" className="mb-0 py-2 px-3">
                                                                    <small className="d-flex align-items-center mb-0">
                                                                        <FaExclamationTriangle className="me-2" />
                                                                        <div>
                                                                            <strong>No Permission</strong>
                                                                            <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                                                                Contact your administrator to request access to this form.
                                                                            </div>
                                                                        </div>
                                                                    </small>
                                                                </Alert>
                                                            )}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    })
                                ) : (
                                    <Col>
                                        <Alert variant="info" className="text-center py-4">
                                            <FaEye className="mb-3" size={32} />
                                            <h5 className="mb-2">
                                                {isParentRole
                                                    ? 'No Completed Forms Available'
                                                    : searchTerm || selectedFolder !== 'all'
                                                        ? 'No matching forms found'
                                                        : 'No forms available'}
                                            </h5>
                                            <p className="text-muted mb-3">
                                                {isParentRole
                                                    ? 'There are no completed clinical forms to view for this patient yet. Forms will appear here once they have been filled out by clinical staff.'
                                                    : searchTerm
                                                        ? `No clinical forms match "${searchTerm}". Try different keywords or clear your search.`
                                                        : selectedFolder !== 'all'
                                                            ? 'No forms found in the selected category. Try a different category.'
                                                            : 'No clinical forms are currently available for this patient.'
                                                }
                                            </p>
                                            {!isParentRole && (searchTerm || selectedFolder !== 'all') && (
                                                <div className="d-flex gap-2 justify-content-center">
                                                    {searchTerm && (
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={() => handleSearchChange('')}
                                                            disabled={isLoadingPaginated}
                                                        >
                                                            <FaTimes className="me-1" />
                                                            Clear Search
                                                        </Button>
                                                    )}
                                                    {selectedFolder !== 'all' && (
                                                        <Button
                                                            variant="outline-secondary"
                                                            onClick={() => handleFolderChange('all')}
                                                            disabled={isLoadingPaginated}
                                                        >
                                                            <FaFilter className="me-1" />
                                                            Show All Categories
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </Alert>
                                    </Col>
                                )}
                            </Row>

                            {/* Custom Pagination */}
                            {displayTotalForms > 0 && (
                                <CustomPagination
                                    currentPage={currentPage}
                                    totalItems={displayTotalForms}
                                    itemsPerPage={itemsPerPage}
                                    onPageChange={handlePageChange}
                                    onItemsPerPageChange={handleItemsPerPageChange}
                                    loading={isLoadingPaginated}
                                />
                            )}
                        </Tab.Pane>

                        {/* TODO: Fix drafts functionality later - commenting out for now */}
                        {/* Draft Forms Tab - Enhanced with Progress Tracking */}
                        {/* <Tab.Pane eventKey="drafts">
                        {draftForms.length > 0 ? (
                            <div>

                                {/* Enhanced Draft List */}
                        {/* <ListGroup>
                                    {draftForms
                                        .sort((a, b) => b.completionPercentage - a.completionPercentage)
                                        .map((draft, index) => {
                                            const progressStatus = getProgressStatus(draft.completionPercentage);
                                            const urgency = getFormUrgency(draft.lastModified);
                                            
                                            return (
                                                <ListGroup.Item key={index} className="draft-form-item">
                                                    <Row className="align-items-center">
                                                        <Col md={8}>
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                <div>
                                                                    <h6 className="mb-1 d-flex align-items-center">
                                                                        {draft.formName}
                                                                        {urgency.isUrgent && (
                                                                            <Badge bg="danger" className="ms-2 small">
                                                                                <FaExclamationTriangle className="me-1" />
                                                                                {urgency.label}
                                                                            </Badge>
                                                                        )}
                                                                    </h6>
                                                                    <small className="text-muted d-flex align-items-center">
                                                                        <FaClock className="me-1" />
                                                                        Last modified: {formatDate(draft.lastModified)}
                                                                    </small>
                                                                </div>
                                                                <Badge bg={progressStatus.color} className="progress-badge">
                                                                    {progressStatus.label}
                                                                </Badge>
                                                            </div>
                                                            
                                                        </Col>
                                                        <Col md={4} className="text-end">
                                                            <div className="d-grid gap-2">
                                                                <Button
                                                                    variant={progressStatus.color}
                                                                    size="sm"
                                                                    onClick={() => handleContinueDraft(draft)}
                                                                    className="d-flex align-items-center justify-content-center"
                                                                >
                                                                    <FaEdit className="me-1" />
                                                                    Continue Editing
                                                                </Button>
                                                                {draft.completionPercentage > 0 && (
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() => handlePreviewDraft(draft)}
                                                                    >
                                                                        <FaEye className="me-1" />
                                                                        Preview
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            );
                                        })}
                                </ListGroup>
                            </div>
                        ) : (
                            <Alert variant="info" className="text-center">
                                <FaClock className="mb-2" size={24} />
                                <p>No draft forms available.</p>
                                <small className="text-muted">
                                    Start filling out a form to create a draft that will auto-save your progress.
                                </small>
                            </Alert>
                        )}
                    </Tab.Pane> */}

                        {/* TODO: Commented out recent tab functionality */}
                        {/* Recent Forms Tab - Enhanced with Completion Tracking */}
                        {/* <Tab.Pane eventKey="recent">
                        {recentForms.length > 0 ? (
                            <div>

                                {/* Enhanced Recent Forms List */}
                        {/* <ListGroup>
                                    {recentForms
                                        .sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))
                                        .map((recent, index) => {
                                            const daysSince = Math.floor((new Date() - new Date(recent.completedDate)) / (1000 * 60 * 60 * 24));
                                            const isRecent = daysSince <= 1;
                                            
                                            return (
                                                <ListGroup.Item key={index} className="recent-form-item">
                                                    <Row className="align-items-center">
                                                        <Col md={8}>
                                                            <div className="d-flex align-items-start">
                                                                <div className="completion-indicator me-3">
                                                                    <div className="completion-badge">
                                                                        <FaCheck className="text-success" />
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-1 d-flex align-items-center">
                                                                        {recent.formName}
                                                                        {isRecent && (
                                                                            <Badge bg="success" className="ms-2 small">
                                                                                New
                                                                            </Badge>
                                                                        )}
                                                                    </h6>
                                                                    <div className="completion-details">
                                                                        <small className="text-muted d-flex align-items-center">
                                                                            <FaCalendarAlt className="me-1" />
                                                                            Completed: {formatDate(recent.completedDate)}
                                                                        </small>
                                                                        <small className="text-muted d-flex align-items-center mt-1">
                                                                            <FaUserMd className="me-1" />
                                                                            By: {recent.completedBy}
                                                                        </small>
                                                                        {daysSince === 0 && (
                                                                            <small className="text-success d-flex align-items-center mt-1">
                                                                                <FaClock className="me-1" />
                                                                                Completed today
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col md={4} className="text-end">
                                                            <div className="d-flex gap-2 justify-content-end">
                                                                <Badge bg="success" className="completion-status">
                                                                    <FaCheck className="me-1" />
                                                                    Completed
                                                                </Badge>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleViewRecentForm(recent)}
                                                                >
                                                                    <FaEye className="me-1" />
                                                                    View
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            );
                                        })}
                                </ListGroup>
                            </div>
                        ) : (
                            <Alert variant="info" className="text-center">
                                <FaCheck className="mb-2" size={24} />
                                <p>No recently completed forms.</p>
                                <small className="text-muted">
                                    Completed forms will appear here once you submit them.
                                </small>
                            </Alert>
                        )}
                    </Tab.Pane> */}
                    </Tab.Content>
                </Tab.Container>
            </Container>

            {/* Submissions List Modal */}
            <Modal 
                show={showSubmissionsModal} 
                onHide={handleCloseSubmissionsModal}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaFileAlt className="me-2" />
                        Form Submissions - {selectedFormForSubmissions?.name || selectedFormForSubmissions?.formName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedFormForSubmissions && formSubmissions[selectedFormForSubmissions.form_id]?.length > 0 ? (
                        <ListGroup>
                            {formSubmissions[selectedFormForSubmissions.form_id].map((submission, index) => (
                                <ListGroup.Item 
                                    key={index}
                                    className="d-flex justify-content-between align-items-center"
                                    action
                                    onClick={() => handleViewSubmissionDetails(submission)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div>
                                        <h6 className="mb-1">
                                            Submission #{index + 1}
                                        </h6>
                                        <small className="text-muted">
                                            <FaCalendarAlt className="me-1" />
                                            Submitted: {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString('en-GB', { hour12: true }) : 'N/A'}
                                        </small>
                                        {submission.submitted_by && (
                                            <small className="text-muted d-block">
                                                <FaUserMd className="me-1" />
                                                By: {submission.submitted_by_name || submission.submitted_by}
                                            </small>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge bg={submission.status === 'submitted' ? 'success' : 'secondary'}>
                                            {submission.status || 'Submitted'}
                                        </Badge>
                                        <FaEye className="text-primary" />
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <Alert variant="info" className="mb-0">
                            <FaFileAlt className="me-2" />
                            No submissions found for this form.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseSubmissionsModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PatientFormsHub;